import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ChevronRight, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useBetaStatus, betaMetric } from "@/hooks/useBetaStatus";

/** Phase 12 — reusable GET WITNESSYOURVOTE UPDATES signup.
 * Variants: "full" (optional name/municipality/ward/postal), "inline" (email row),
 * "footer" (compact dark). Consent is ALWAYS an active checkbox — never pre-ticked,
 * never automatic. Under EMAIL MODE: SIMULATED nothing external is sent; the record is
 * stored with consent text + timestamp and the welcome message goes to the admin log. */
export const LaunchSignup = ({ variant = "full", sourcePage, className = "" }) => {
  const beta = useBetaStatus();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [ward, setWard] = useState("");
  const [postal, setPostal] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const startedRef = useRef(false);
  const msg = beta?.messaging || {};

  const onStart = () => {
    if (!startedRef.current) { startedRef.current = true; betaMetric("signup_start"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please tick the consent box so we may email you launch updates."); return; }
    setBusy(true);
    try {
      const r = await api.post("/public/beta/subscribe", {
        email: email.trim(),
        first_name: firstName.trim() || null,
        municipality: municipality.trim() || null,
        ward: ward.trim() || null,
        postal_area: postal.trim() || null,
        consent: true,
        source_page: sourcePage || window.location.pathname,
        website: "",
      });
      setDone(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete your signup — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    const dark = variant === "footer";
    return (
      <div className={`${dark ? "text-slate-200" : ""} ${className}`} data-testid="launch-signup-success">
        <p className={`font-heading font-bold ${dark ? "text-white text-sm" : "text-foreground text-lg"} inline-flex items-center gap-2`}>
          <CheckCircle2 className={`h-5 w-5 ${dark ? "text-emerald-400" : "text-accent"}`} />
          {done.title || "YOU'RE ON THE LIST"}
        </p>
        <p className={`mt-1 ${dark ? "text-xs text-slate-400" : "text-sm text-muted-foreground"}`}>{done.body}</p>
        <Link to="/how-it-works"
              className={`inline-flex items-center gap-1 mt-2 text-sm font-medium ${dark ? "text-emerald-400 hover:text-emerald-300" : "text-primary hover:underline"}`}
              data-testid="launch-signup-explore">
          Explore WitnessYourVote <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  const consentLabel = msg.consent_label || "Yes, send me WitnessYourVote launch updates and important platform news.";

  if (variant === "footer") {
    return (
      <form onSubmit={submit} className={className} data-testid="launch-signup-footer">
        <div className="flex flex-col sm:flex-row gap-2">
          <label htmlFor="footer-signup-email" className="sr-only">Email address</label>
          <Input id="footer-signup-email" type="email" required placeholder="you@example.com"
                 value={email} onChange={(e) => setEmail(e.target.value)} onFocus={onStart}
                 className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-9 text-sm"
                 data-testid="launch-signup-email" />
          <Button type="submit" size="sm" disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 shrink-0"
                  data-testid="launch-signup-submit">
            {busy ? "…" : "SIGN UP"}
          </Button>
        </div>
        <label className="flex items-start gap-2 mt-2 cursor-pointer">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-slate-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                    data-testid="launch-signup-consent" />
          <span className="text-[11px] text-slate-400 leading-snug">{consentLabel}</span>
        </label>
        <p className="text-[10px] text-slate-500 mt-1.5">No spam. Unsubscribe anytime.</p>
      </form>
    );
  }

  if (variant === "inline") {
    return (
      <form onSubmit={submit} className={className} data-testid="launch-signup-inline">
        <div className="flex flex-col sm:flex-row gap-2 max-w-lg">
          <label htmlFor="inline-signup-email" className="sr-only">Email address</label>
          <Input id="inline-signup-email" type="email" required placeholder="Email address"
                 value={email} onChange={(e) => setEmail(e.target.value)} onFocus={onStart}
                 className="flex-1 bg-white" data-testid="launch-signup-email" />
          <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                  data-testid="launch-signup-submit">
            <Mail className="h-4 w-4 mr-1.5" /> {busy ? "Joining…" : "GET ON THE LIST"}
          </Button>
        </div>
        <label className="flex items-start gap-2 mt-2.5 cursor-pointer max-w-lg">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5"
                    data-testid="launch-signup-consent" />
          <span className="text-[11px] leading-snug opacity-90">{consentLabel}</span>
        </label>
        <p className="text-[10px] opacity-70 mt-1.5">No spam. Unsubscribe anytime.</p>
      </form>
    );
  }

  // full variant
  return (
    <Card className={className} data-testid="launch-signup-full">
      <CardContent className="p-6">
        <p className="font-heading font-bold text-base text-foreground inline-flex items-center gap-2">
          <Mail className="h-4 w-4 text-accent" /> GET WITNESSYOURVOTE UPDATES
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Join the early list today. We'll send you an update when WitnessYourVote officially goes live.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <Label htmlFor="signup-email" className="text-xs">Email address <span className="text-destructive">*</span></Label>
            <Input id="signup-email" type="email" required value={email}
                   onChange={(e) => setEmail(e.target.value)} onFocus={onStart}
                   className="mt-1" data-testid="launch-signup-email" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="signup-first" className="text-xs">First name <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="signup-first" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                     className="mt-1" maxLength={80} data-testid="launch-signup-firstname" />
            </div>
            <div>
              <Label htmlFor="signup-muni" className="text-xs">Municipality <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="signup-muni" value={municipality} onChange={(e) => setMunicipality(e.target.value)}
                     className="mt-1" maxLength={120} placeholder="e.g. Kawartha Lakes" data-testid="launch-signup-municipality" />
            </div>
            <div>
              <Label htmlFor="signup-ward" className="text-xs">Ward, if known <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="signup-ward" value={ward} onChange={(e) => setWard(e.target.value)}
                     className="mt-1" maxLength={80} data-testid="launch-signup-ward" />
            </div>
            <div>
              <Label htmlFor="signup-postal" className="text-xs">Postal code / area <span className="text-muted-foreground">(optional — municipality matching only)</span></Label>
              <Input id="signup-postal" value={postal} onChange={(e) => setPostal(e.target.value)}
                     className="mt-1" maxLength={20} data-testid="launch-signup-postal" />
            </div>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-slate-200 bg-secondary/60 p-3">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5"
                      data-testid="launch-signup-consent" />
            <span className="text-xs text-foreground leading-snug">{consentLabel}</span>
          </label>
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            {msg.consent_fine_print || "No spam. Unsubscribe anytime. Your information is never sold, never shared with candidates, and is stored completely separately from any voter declaration data."}
          </p>
          <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
                  data-testid="launch-signup-submit">
            {busy ? "Joining…" : "GET ON THE LIST"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
